// eslint-disable @typescript-eslint/no-explicit-any
import { createContext, useContext, useEffect } from "react";
import scrollIntoViewIfNeeded from "scroll-into-view-if-needed";
import type { TypeMenuButtonContext, TypeMenuContext } from "../MenuTypes";
import { MENU_ROLES } from "./constants";
import type { TypeDescendant } from "./descendants";
import {
  createDescendantContext,
  useDescendantKeyDown,
  usePrevious,
  wrapEvent,
} from "./descendants";
export const MenuContext = createContext<TypeMenuContext>({
  role: MENU_ROLES.MENU,
  menuRef: {
    current: null,
  },
  value: undefined,
  multiselect: false,
  onChange: undefined,
  state: {
    selectionIndex: 0,
    filterQuery: "",
    filteredItems: null,
    isFilterInputFocused: false,
    isFooterFocused: false,
  },
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setState: () => {},
  selectCallbacks: {
    current: [],
  },
});
export const MenuButtonContext = createContext<TypeMenuButtonContext>({
  closeOnItemClick: undefined,
  closePopout: undefined,
});
export type TypeMenuDescendantProps = {
  id: string;
  label: string | null | undefined;
  value?: string;
  disabled?: boolean;
};
export const MenuDescendantContext = createDescendantContext(
  "MenuDescendantContext"
);

/**
 * When a user's typed input matches the string displayed in a menu item, it is
 * expected that the matching menu item is selected. This is our matching
 * function.
 */
function filterItemsFromTypeahead(
  items: TypeDescendant<HTMLElement, TypeMenuDescendantProps>[],
  string = ""
) {
  if (!string) {
    return null;
  }

  return items
    .filter(
      ({ value, label }) =>
        value?.toLowerCase().includes(string.toLocaleLowerCase()) ||
        label?.toLowerCase().includes(string.toLocaleLowerCase())
    )
    .map(({ index }) => index);
}

export function useMenuKeyDown() {
  const {
    onChange,
    selectCallbacks,
    setState,
    multiselect,
    state: {
      filterQuery,
      selectionIndex,
      filteredItems,
      isFilterInputFocused,
      isFooterFocused,
    },
  } = useContext(MenuContext);
  const { closePopout, closeOnItemClick } = useContext(MenuButtonContext);
  const { descendants: items } = useContext(MenuDescendantContext);
  const shouldClosePopout = closeOnItemClick && !multiselect;
  const prevMenuItemsLength = usePrevious(items.length);
  const prevSelected = usePrevious(items[selectionIndex]);
  const prevSelectionIndex = usePrevious(selectionIndex);
  useEffect(() => {
    if (items.length && selectionIndex > items.length - 1) {
      // If for some reason our selection index is larger than our possible
      // index range (let's say the last item is selected and the list
      // dynamically updates), we need to select the last item in the list.
      setState({
        selectionIndex: items.length - 1,
      });
    } else if (
      // Checks if
      //  - menu length has changed
      //  - selection index has not changed BUT selected item has changed
      //
      // This prevents any dynamic adding/removing of menu items from actually
      // changing a user's expected selection.
      prevMenuItemsLength !== items.length &&
      selectionIndex > -1 &&
      prevSelected &&
      prevSelectionIndex === selectionIndex &&
      items[selectionIndex] !== prevSelected
    ) {
      setState({
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore I'm just going to ignore this one because its really hard to estimate where this stuff is coming from at any given time
        selectionIndex: items.findIndex((i) => i.index === prevSelected.index),
      });
    }
  }, [
    setState,
    items,
    prevMenuItemsLength,
    prevSelected,
    prevSelectionIndex,
    selectionIndex,
  ]);
  useEffect(() => {
    const newFilteredItems = filterItemsFromTypeahead(items, filterQuery);

    if (
      filterQuery &&
      newFilteredItems &&
      filteredItems?.length !== newFilteredItems?.length
    ) {
      setState({
        filteredItems: newFilteredItems,
        selectionIndex: newFilteredItems[0],
      });
    } else if (!filterQuery && filteredItems !== null) {
      setState({
        filteredItems: null,
      });
    }
  }, [filterQuery, items, setState, filteredItems]);
  const index = items.findIndex(({ index }) => index === selectionIndex);
  return {
    activeDescendent: items[selectionIndex],
    handleKeyDown: wrapEvent<React.KeyboardEvent>(
      function (event) {
        const { key } = event;
        // Do not trigger key events for the selected item if the footer is focused
        if (isFooterFocused) {
          return;
        }

        switch (key) {
          case " ":
            if (isFilterInputFocused) {
              return;
            }

          // falls through
          // eslint-disable-next-line no-fallthrough
          case "Enter":
            // eslint-disable-next-line no-case-declarations
            const selected = items[selectionIndex] as HTMLElement[] & {
              index: number;
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              value: any;
              disabled?: boolean;
            };

            if (selected && !selected.disabled) {
              event.preventDefault();
              const func = selectCallbacks.current[selected.index];
              func?.call && func();
              onChange && selected.value && onChange(selected.value);
              shouldClosePopout && closePopout && closePopout();
            }

            return;

          default:
            return;
        }
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      useDescendantKeyDown(MenuDescendantContext as React.Context<any>, {
        currentIndex: index,
        orientation: "vertical",
        key: "option",
        rotate: true,
        filter: (
          option: TypeDescendant<HTMLElement, TypeMenuDescendantProps>
        ) =>
          !option.disabled &&
          (!filteredItems || filteredItems.includes(option.index)),

        callback(item: TypeDescendant<HTMLElement, TypeMenuDescendantProps>) {
          if (item.element) {
            scrollIntoViewIfNeeded(item.element, {
              block: "nearest",
              scrollMode: "if-needed",
            });
          }

          setState({
            selectionIndex: item.index,
          });
        },
      })
    ),
  };
}
